import { isAxiosError } from "axios";
import { api } from "@/lib/api";

export interface AddressSettings { m2_per_person: string; retention_months: number }
export interface AddressPurpose { code: string; name_hr: string }
export interface AddressInput {
  street: string; city: string; postal_code: string; landlord_name: string;
  landlord_oib: string | null; lease_document_id: string | null;
  area_m2: string; capacity: number | null; purpose_code: string; active: boolean;
  note: string | null; confirm_capacity_override?: boolean;
}
export interface RegistrationAddress extends AddressInput {
  id: string; capacity: number; capacity_override: boolean; suggested_capacity: number;
  occupied: number; available: number; available_from: string | null;
}
export interface AddressRegistration {
  id: string; worker_id: string; worker_name: string; address_id: string;
  starts_on: string; ends_on: string | null; source: string; occupied_until: string | null;
  state: "registered" | "holding" | "history" | "planned"; capacity_override: boolean;
}
export interface RegistrationInput {
  worker_ids: string[]; starts_on: string; ends_on?: string | null;
  source: "manual" | "address_change" | "initial"; confirm_full?: boolean;
}
export interface AddressDetail { address: RegistrationAddress; registrations: AddressRegistration[] }
export interface WorkerAddress { address: RegistrationAddress | null; registration: AddressRegistration | null; missing: boolean }
export interface AddressWarning { worker_id: string; worker_name: string }
export interface AddressChange { change_id: string }
const root = "/registration-addresses";
export async function getAddresses(params: { on_date?: string; purpose?: string; available_only?: boolean } = {}, signal?: AbortSignal) {
  return (await api.get<RegistrationAddress[]>(root, { params, signal })).data;
}
export async function getAddress(id: string, signal?: AbortSignal) { return (await api.get<AddressDetail>(`${root}/${id}`, { signal })).data; }
export async function saveAddress(body: AddressInput, id?: string) {
  return (id ? await api.put<RegistrationAddress>(`${root}/${id}`, body) : await api.post<RegistrationAddress>(root, body)).data;
}
export async function getAddressSettings(signal?: AbortSignal) { return (await api.get<AddressSettings>(`${root}/settings`, { signal })).data; }
export async function saveAddressSettings(body: AddressSettings) { return (await api.put<AddressSettings>(`${root}/settings`, body)).data; }
export async function getAddressPurposes(signal?: AbortSignal) { return (await api.get<AddressPurpose[]>(`${root}/purposes`, { signal })).data; }
export async function registerWorkers(id: string, body: RegistrationInput) { return (await api.post<AddressChange>(`${root}/${id}/registrations`, body)).data; }
export async function checkoutRegistration(id: string, ends_on: string) { return (await api.post<AddressChange>(`${root}/registrations/${id}/checkout`, { ends_on })).data; }
export async function undoAddressChange(id: string) { return (await api.post(`${root}/changes/${id}/undo`)).data as { undone_at: string }; }
export async function getWorkerAddress(id: string, signal?: AbortSignal) { return (await api.get<WorkerAddress>(`${root}/workers/${id}`, { signal })).data; }
export async function getMissingAddresses(signal?: AbortSignal) { return (await api.get<AddressWarning[]>(`${root}/missing`, { signal })).data; }
export function addressConfirmation(error: unknown): { code: string; message: string } | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const detail: unknown = error.response.data?.detail;
  if (typeof detail !== "object" || detail === null || !("code" in detail) || !("message" in detail)) return null;
  return (detail.code === "capacity_confirmation_required" || detail.code === "full_confirmation_required") && typeof detail.message === "string" ? { code: detail.code, message: detail.message } : null;
}
export function addressTone(address: RegistrationAddress): string {
  return !address.active ? "bg-gray-100 text-gray-800" : address.available === 0 ? "bg-red-100 text-red-900" : address.available === 1 ? "bg-orange-100 text-orange-950" : "bg-green-100 text-green-950";
}
export function capacityProposal(area: string, perPerson: string): number | null {
  // The API accepts two decimal places. Integer hundredths avoid 0.3 / 0.1
  // being rounded down to two by floating-point division in the preview.
  const areaUnits = Math.round(Number(area) * 100);
  const personUnits = Math.round(Number(perPerson) * 100);
  return Number.isFinite(areaUnits) && areaUnits > 0 && Number.isFinite(personUnits) && personUnits > 0 ? Math.floor(areaUnits / personUnits) : null;
}
